import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaDesktop, FaShieldAlt, FaLock, FaNetworkWired, FaCheck } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const LeftSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  max-height: 400px;
  animation: ${slideInLeft} 1s ease-in-out;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ExperienceBadge = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 10px 18px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const FeatureCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  background: ${props => props.bgColor || 'linear-gradient(to right, #228B22, #E60000)'};
  color: white;
  font-size: 24px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
`;

const FeatureTitle = styled.h4`
  font-weight: bold;
  margin-bottom: 12px;
  color: #333;
  font-size: 20px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  font-size: 16px;
  color: #555;
  
  svg {
    color: #228B22;
    margin-right: 10px;
    margin-top: 4px;
    flex-shrink: 0;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
  }
  
  svg {
    font-size: 40px;
    margin-bottom: 15px;
  }
  
  p {
    font-weight: bold;
    margin-top: 10px;
    font-size: 18px;
  }
`;

const SectionTitle = styled.h3`
  position: relative;
  font-weight: bold;
  margin-bottom: 40px;
  padding-bottom: 15px;
  text-align: center;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, #228B22, #E60000);
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const ITSecuritySolutions = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    { 
      question: "Pourquoi un cabinet d'expertise comptable propose-t-il des solutions informatiques et de sécurité ?", 
      answer: "Les cabinets d'expertise comptable manipulent des données sensibles et ont une expertise dans l'organisation et la gestion de l'information. Ils sont donc bien placés pour conseiller et fournir des solutions informatiques et de sécurité adaptées aux besoins des entreprises." 
    },
    { 
      question: "Comment les solutions informatiques et de sécurité peuvent-elles aider mon entreprise ?", 
      answer: "Elles peuvent améliorer la sécurité des données, optimiser les processus, faciliter la collaboration, réduire les coûts, et assurer la conformité aux réglementations." 
    },
    { 
      question: "Quels services proposez-vous en matière de sécurité informatique ?", 
      answer: "Nous développons des applications utiles et sécurisées pour les besoins de nos clients, nous proposons des audits de sécurité, la mise en place de solutions de protection et de sauvegarde, la formation à la cybersécurité, et des solutions sécurisées de vidéos surveillance." 
    },
    { 
      question: "Pouvez-vous m'aider à sécuriser mes données sensibles ?", 
      answer: "Oui, nous vous conseillons sur les meilleures pratiques de protection des données, la mise en place de systèmes de sauvegarde et de chiffrement, et la gestion des accès." 
    },
    { 
      question: "Proposez-vous des solutions de sauvegarde de données ?", 
      answer: "Oui, nous proposons des solutions de sauvegarde locales et externalisées, pour assurer la protection et la restauration de vos données en cas de besoin." 
    },
    { 
      question: "Proposez-vous des solutions de gestions de logiciels et de matériels informatiques ?", 
      answer: "Oui, nous proposons la conception, l'installation et les mises à jour de logiciels. Nous ne vendons pas de matériel informatique." 
    },
  ];

  return (
    <>
      <Header />

      <section className="service-banner d-flex align-items-center">
        <div className="container text-white text-start">
          <h1 className="fw-bold">Solutions Informatiques et de Sécurité</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white text-decoration-none">
                  Accueil
                </Link>{" "}
                /{" "}
                <Link to="/services" className="text-white text-decoration-none">
                  Services
                </Link>
              </li>
              <li className="breadcrumb-item active text-white" aria-current="page">
                Solutions IT & Sécurité
              </li>
            </ol>
          </nav>
        </div>
      </section>

      <section className="service-content py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="fw-bold">Sécurisez votre environnement numérique</h2>
              <div className="separator mx-auto my-3"></div>
              <p className="lead text-muted">
                Les données sont au cœur de votre activité et leur protection est devenue une priorité absolue. 
                CINEF-EC vous accompagne dans la sécurisation de votre environnement numérique, en vous proposant 
                des solutions informatiques adaptées à vos besoins et à vos enjeux.
              </p>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-md-6">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                  <FaShieldAlt />
                </FeatureIcon>
                <FeatureTitle>Expertise intégrée</FeatureTitle>
                <p>
                  Nous combinons notre expertise comptable et financière avec une solide connaissance 
                  des enjeux de sécurité informatique, pour vous offrir une vision globale et des solutions adaptées.
                </p>
              </FeatureCard>
            </div>
            <div className="col-md-6">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                  <FaLock />
                </FeatureIcon>
                <FeatureTitle>Protection des données sensibles</FeatureTitle>
                <p>
                  Nous savons à quel point vos données financières et clients sont précieuses. 
                  Nous mettons en œuvre avec nos partenaires des solutions de protection robustes 
                  pour garantir leur confidentialité et leur intégrité.
                </p>
              </FeatureCard>
            </div>
            <div className="col-md-6">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #228B22, #32CD32)">
                  <FaNetworkWired />
                </FeatureIcon>
                <FeatureTitle>Conformité réglementaire</FeatureTitle>
                <p>
                  Nous vous aidons à vous conformer aux réglementations en vigueur en matière 
                  de protection des données et à mettre en place les procédures nécessaires 
                  pour assurer votre conformité.
                </p>
              </FeatureCard>
            </div>
            <div className="col-md-6">
              <FeatureCard>
                <FeatureIcon bgColor="linear-gradient(135deg, #E60000, #FF6B6B)">
                  <FaDesktop />
                </FeatureIcon>
                <FeatureTitle>Accompagnement personnalisé</FeatureTitle>
                <p>
                  Nous vous conseillons sur les meilleures pratiques de sécurité, la mise en place 
                  de systèmes de sauvegarde efficaces, et la gestion des accès. Nous créons et développons 
                  des applications ou d'interfaces sur-mesure pour répondre à des besoins métiers particuliers.
                </p>
              </FeatureCard>
            </div>
          </div>
        </div>
      </section>

      <section className="advantages py-5">
        <div className="container">
          <SectionTitle>Nos Services Informatiques</SectionTitle>
          <div className="row g-4">
            <div className="col-md-3">
              <ServiceCard>
                <FaDesktop className="text-primary" />
                <p>Support Technique</p>
              </ServiceCard>
            </div>
            <div className="col-md-3">
              <ServiceCard>
                <FaShieldAlt className="text-success" />
                <p>Cybersécurité</p>
              </ServiceCard>
            </div>
            <div className="col-md-3">
              <ServiceCard>
                <FaLock className="text-danger" />
                <p>Protection des Données</p>
              </ServiceCard>
            </div>
            <div className="col-md-3">
              <ServiceCard>
                <FaNetworkWired className="text-warning" />
                <p>Infrastructure Réseau</p>
              </ServiceCard>
            </div>
          </div>
        </div>
      </section>

      <section id="faq-section" className="container-fluid py-5 bg-light">
        <div className="container">
          <SectionTitle>Questions Fréquentes</SectionTitle>
          <FAQContainer>
            
            
            <FAQContent>
              {faqData.map((faq, index) => (
                <FAQItem key={index} delay={`${index * 0.2}s`}>
                  <Question onClick={() => toggleFAQ(index)}>
                    {faq.question}
                    <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                  </Question>
                  {activeIndex === index && <Answer>{faq.answer}</Answer>}
                </FAQItem>
              ))}
            </FAQContent>
          </FAQContainer>
        </div>
      </section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.8), rgba(220, 53, 69, 0.8)), url("/assets/images/service-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }
        
        .separator {
          height: 4px;
          width: 80px;
          background: linear-gradient(to right, #228B22, #E60000);
          border-radius: 2px;
        }
        
        .bg-light {
          background-color: #f8f9fa;
        }
        
        .lead {
          font-size: 1.2rem;
          line-height: 1.6;
        }
      `}</style>
    </>
  );
};

export default ITSecuritySolutions;